import {
	InputHTMLAttributes,
	TextareaHTMLAttributes,
	forwardRef,
	type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, Search } from "lucide-react";
import { useState } from "react";

// =====================
// Input Component
// =====================

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
	label?: string;
	error?: string;
	helperText?: string;
	leftIcon?: ReactNode;
	rightIcon?: ReactNode;
	showPasswordToggle?: boolean;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
	({ className, type, label, error, helperText, leftIcon, rightIcon, showPasswordToggle, id, ...props }, ref) => {
		const [showPassword, setShowPassword] = useState(false);
		const inputId = id || `input-${Math.random().toString(36).slice(2)}`;
		const isPassword = type === "password";
		const isSearch = type === "search";

		return (
			<div className="w-full">
				{label && (
					<label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						{label}
					</label>
				)}
				<div className="relative">
					{(leftIcon || isSearch) && (
						<div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
							{leftIcon || <Search className="h-4 w-4" />}
						</div>
					)}
					<input
						ref={ref}
						id={inputId}
						type={isPassword ? (showPassword ? "text" : "password") : type}
						className={cn(
							"flex h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm transition-colors duration-150 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100",
							error ? "border-red-500 focus:ring-red-500" : "border-gray-300 dark:border-gray-600",
							leftIcon || isSearch ? "pl-10" : "",
							(isPassword || showPasswordToggle || rightIcon) && "pr-10",
							"dark:bg-gray-900 dark:text-gray-100",
							className
						)}
						aria-invalid={error ? "true" : undefined}
						aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
						{...props}
					/>
					{(rightIcon || isPassword || showPasswordToggle) && (
						<div className="absolute right-3 top-1/2 -translate-y-1/2">
							{isPassword ? (
								<button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:text-brand-500" tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
									{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</button>
							) : rightIcon}
						</div>
					)}
				</div>
				{error && <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-500" role="alert">{error}</p>}
				{!error && helperText && <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>}
			</div>
		);
	}
);

Input.displayName = "Input";

// =====================
// Textarea Component
// =====================

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
	label?: string;
	error?: string;
	helperText?: string;
};

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ className, label, error, helperText, id, ...props }, ref) => {
		const inputId = id || `textarea-${Math.random().toString(36).slice(2)}`;
		return (
			<div className="w-full">
				{label && <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{label}</label>}
				<textarea
					ref={ref}
					id={inputId}
					className={cn(
						"flex min-h-[100px] w-full rounded-lg border bg-white px-3 py-2 text-sm transition-colors duration-150 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100",
						error ? "border-red-500 focus:ring-red-500" : "border-gray-300 dark:border-gray-600",
						"dark:bg-gray-900 dark:text-gray-100",
						className
					)}
					aria-invalid={error ? "true" : undefined}
					aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
					{...props}
				/>
				{error && <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-500" role="alert">{error}</p>}
				{!error && helperText && <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>}
			</div>
		);
	}
);

Textarea.displayName = "Textarea";

export { Input, Textarea };